/**
 * Buyer "request a listing" — shared validation and shaping.
 * Seller identity never appears in a BuyerListingRequest.
 */
export type ListingRequestStatus = "open" | "listed" | "declined" | "cancelled";

export interface ListingRequestInput {
  quantity: string;
  unit: "tons" | "cubic-yards";
  neededBy: string;
  deliveryZip: string;
  note: string;
}

export interface BuyerListingRequest {
  id: string;
  labReportId: string;
  title: string;
  quantity: number;
  unit: string;
  neededBy: string | null;
  deliveryZip: string;
  note: string;
  status: ListingRequestStatus;
  listingId: string | null;
  createdAt: string;
}

export const STATUS_LABELS: Record<ListingRequestStatus, string> = {
  open: "Waiting for seller",
  listed: "Listed",
  declined: "Declined",
  cancelled: "Cancelled",
};

export function validateListingRequest(i: ListingRequestInput): string | null {
  const q = Number(i.quantity);
  if (!i.quantity.trim() || !Number.isFinite(q) || q <= 0) return "Enter the quantity you need.";
  if (q > 10_000_000) return "Quantity is too large.";
  if (i.deliveryZip && !/^\d{5}$/.test(i.deliveryZip)) return "Delivery ZIP must be 5 digits.";
  if (i.note.length > 1000) return "Note must be 1000 characters or fewer.";
  if (i.neededBy && Number.isNaN(Date.parse(i.neededBy))) return "Enter a valid date.";
  return null;
}

export function toListingRequestRow(i: ListingRequestInput) {
  return {
    quantity: Number(i.quantity),
    unit: i.unit,
    needed_by: i.neededBy || null,
    delivery_zip: i.deliveryZip,
    note: i.note.trim(),
  };
}

type Raw = Record<string, unknown>;

/** Only whitelisted keys survive — seller fields are dropped even if sent. */
export function toBuyerListingRequests(rows: unknown[]): BuyerListingRequest[] {
  return (rows as Raw[]).map((r) => ({
    id: String(r.id),
    labReportId: String(r.lab_report_id),
    title: String(r.title ?? "Lab report"),
    quantity: Number(r.quantity),
    unit: String(r.unit),
    neededBy: (r.needed_by as string) ?? null,
    deliveryZip: String(r.delivery_zip ?? ""),
    note: String(r.note ?? ""),
    status: r.status as ListingRequestStatus,
    listingId: (r.listing_id as string) ?? null,
    createdAt: String(r.created_at),
  }));
}

/** A library report can be requested when it has no listing for sale and the viewer has no open request. */
export function canRequest(listingId: string | null, openReportIds: Set<string>, reportId: string): boolean {
  return !listingId && !openReportIds.has(reportId);
}
